/**
 * Lightweight client-side performance monitor for the virtual-tour viewer.
 *
 * Tracks the four numbers that matter for panorama navigation:
 *   • cache hit rate   — share of panorama loads served from memory/SW/disk
 *   • asset load time  — ms to obtain a panorama (network vs. cache)
 *   • scene transition — ms from "navigate" to "panorama visible"
 *   • LCP              — Largest Contentful Paint of the initial view
 *
 * Zero dependencies, no network calls. Everything is exposed on
 * `window.__pvtPerf` so it can be inspected live in the console, and a compact
 * summary is logged in dev. Designed to be safe to call from anywhere.
 */

export type CacheTier = 'memory' | 'sw' | 'network';

interface LoadSample {
  url: string;
  tier: CacheTier;
  ms: number;
  at: number;
}

interface TransitionSample {
  from: string | null;
  to: string;
  ms: number;
  at: number;
}

const isDev = (import.meta as any).env?.DEV ?? false;

const state = {
  loads: [] as LoadSample[],
  transitions: [] as TransitionSample[],
  lcp: 0,
  hits: 0,
  misses: 0,
};

const round = (n: number) => Math.round(n * 10) / 10;

/** Record a single panorama asset load and its cache tier. */
export function recordLoad(url: string, tier: CacheTier, ms: number): void {
  state.loads.push({ url, tier, ms: round(ms), at: Date.now() });
  if (tier === 'network') state.misses++;
  else state.hits++;
  if (state.loads.length > 200) state.loads.shift();
  if (isDev) {
    const tag = tier === 'network' ? 'MISS' : `HIT:${tier}`;
    // eslint-disable-next-line no-console
    console.debug(`[perf] panorama ${tag} ${round(ms)}ms`, shortUrl(url));
  }
}

/** Record a scene transition (navigate → panorama painted). */
export function recordTransition(from: string | null, to: string, ms: number): void {
  state.transitions.push({ from, to, ms: round(ms), at: Date.now() });
  if (state.transitions.length > 200) state.transitions.shift();
  if (isDev) {
    // eslint-disable-next-line no-console
    console.debug(`[perf] transition ${round(ms)}ms → ${to}`);
  }
}

/** Aggregate snapshot — also what gets returned from window.__pvtPerf.summary(). */
export function summary() {
  const total = state.hits + state.misses;
  const avg = (xs: number[]) =>
    xs.length ? round(xs.reduce((a, b) => a + b, 0) / xs.length) : 0;
  return {
    cacheHitRate: total ? round((state.hits / total) * 100) : 0,
    loads: total,
    avgLoadMs: avg(state.loads.map((l) => l.ms)),
    avgNetworkMs: avg(state.loads.filter((l) => l.tier === 'network').map((l) => l.ms)),
    avgCachedMs: avg(state.loads.filter((l) => l.tier !== 'network').map((l) => l.ms)),
    avgTransitionMs: avg(state.transitions.map((t) => t.ms)),
    lcpMs: round(state.lcp),
  };
}

function shortUrl(url: string): string {
  try {
    const u = new URL(url, location.href);
    return u.pathname.split('/').pop() || url;
  } catch {
    return url;
  }
}

let started = false;

/** Initialise LCP observation. Call once at app start. Idempotent. */
export function initPerfMonitor(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  (window as any).__pvtPerf = {
    summary,
    get loads() { return state.loads; },
    get transitions() { return state.transitions; },
  };

  if ('PerformanceObserver' in window) {
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // LCP reports the largest element seen so far; keep the latest.
          state.lcp = (entry as any).renderTime || (entry as any).loadTime || entry.startTime;
        }
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {
      /* LCP not supported — ignore */
    }
  }

  if (isDev) {
    // Print a summary shortly after load settles.
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.info('[perf] summary', summary());
    }, 8000);
  }
}
